import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, Chip, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import {
    Gavel as AuditIcon, FilterList as FilterIcon, Search as SearchIcon, Visibility as ViewIcon, Warning as WarningIcon, CheckCircle as InfoIcon, Error as ErrorIcon, Security as SecurityIcon, Person as UserIcon,
    // FIX 1 & 2: Added CloseIcon and GavelIcon (as requested by error report)
    Close as CloseIcon,
    Gavel as GavelIcon
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

// --- MOCK Data ---
const mockLogs = [
    { id: 1, timestamp: '2025-10-03 13:55:00', user: 'Admin', action: 'User Created', resource: 'User: Jane Doe', severity: 'Critical', ip: '192.168.1.10', details: { change: 'User "Jane Doe" added to "Engineering" Dept.', before: {}, after: { name: 'Jane Doe', dept: 'Engineering' } } },
    { id: 2, timestamp: '2025-10-03 13:54:30', user: 'API-Service', action: 'Data Sync', resource: 'MasterData: Locations', severity: 'Info', ip: '10.0.0.5', details: { status: 'Successful sync', records_updated: 5 } },
    { id: 3, timestamp: '2025-10-03 13:53:10', user: 'MariaG', action: 'Login Failed', resource: 'Authentication', severity: 'Warning', ip: '203.0.113.44', details: { reason: 'Incorrect Password', attempts: 3 } },
    { id: 4, timestamp: '2025-10-03 13:52:05', user: 'Admin', action: 'Policy Deleted', resource: 'Policy: Travel', severity: 'Critical', ip: '192.168.1.10', details: { policy_id: 'POL-003', archived: true } },
    { id: 5, timestamp: '2025-10-03 13:51:15', user: 'BenS', action: 'View Report', resource: 'Report: Financials', severity: 'Info', ip: '172.16.0.12', details: { report_name: 'Q3 Budget Summary' } },
];

const getSeverityChip = (severity) => {
    switch (severity) {
        case 'Critical': return <Chip label="Critical" size="small" color="error" icon={<ErrorIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 700 }} />;
        case 'Warning': return <Chip label="Warning" size="small" color="warning" icon={<WarningIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 700 }} />;
        case 'Info': return <Chip label="Info" size="small" color="success" icon={<InfoIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 700 }} />;
        default: return <Chip label={severity} size="small" />;
    }
};

// --- Column Definitions for DataGrid ---
const getColumns = (handleViewDetails) => [
    { field: 'timestamp', headerName: 'Timestamp', width: 170, renderHeader: () => <Box fontWeight={700}>Time</Box> },
    { field: 'user', headerName: 'User/Service', width: 150, renderHeader: () => <Box fontWeight={700}>User</Box> },
    { field: 'action', headerName: 'Action Type', width: 150, renderHeader: () => <Box fontWeight={700}>Action</Box> },
    { field: 'resource', headerName: 'Resource Affected', flex: 1, minWidth: 200, renderHeader: () => <Box fontWeight={700}>Resource</Box> },
    { field: 'ip', headerName: 'IP Address', width: 120, renderHeader: () => <Box fontWeight={700}>IP</Box> },
    { 
        field: 'severity', 
        headerName: 'Severity', 
        width: 120,
        renderCell: (params) => getSeverityChip(params.value),
        renderHeader: () => <SecurityIcon fontSize="small" color="error" />
    },
    {
        field: 'actions',
        headerName: 'Details',
        width: 100,
        sortable: false,
        renderCell: (params) => (
            <Tooltip title="View Full Log Details">
                <IconButton size="small" onClick={() => handleViewDetails(params.row)}>
                    <ViewIcon color="primary" fontSize="small" />
                </IconButton>
            </Tooltip>
        ),
    },
];

// --- Sub-Component: Log Detail Dialog ---
const LogDetailDialog = ({ open, handleClose, log, theme }) => (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: theme.palette.error.dark, color: 'white', fontWeight: 600 }}>
            <AuditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Audit Log Entry #{log?.id || ''}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="subtitle2">Timestamp:</Typography><Typography variant="body1" fontWeight={600}>{log?.timestamp}</Typography></Grid>
                <Grid item xs={6}><Typography variant="subtitle2">User:</Typography><Typography variant="body1" fontWeight={600}>{log?.user}</Typography></Grid>
                <Grid item xs={6}><Typography variant="subtitle2">Action:</Typography><Typography variant="body1" fontWeight={600}>{log?.action}</Typography></Grid>
                <Grid item xs={6}><Typography variant="subtitle2">IP Address:</Typography><Typography variant="body1" fontWeight={600}>{log?.ip}</Typography></Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Raw Details (JSON):</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.grey[50], overflowX: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(log?.details, null, 2)}
                        </pre>
                    </Paper>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
            {/* FIX: CloseIcon is now imported */}
            <Button onClick={handleClose} color="error" variant="contained" startIcon={<CloseIcon />}>
                Close
            </Button>
        </DialogActions>
    </Dialog>
);


// --- Main Audit Logs Component ---
const AuditLogs = () => {
    const theme = useTheme();
    const [logs, setLogs] = useState(mockLogs);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [filter, setFilter] = useState({ severity: 'All', user: '' });

    const handleViewDetails = (log) => {
        setSelectedLog(log);
        setOpenDialog(true);
    };

    const handleFilterChange = (e) => {
        setFilter(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    // Filter logic (Simplified client-side filtering)
    const filteredLogs = logs.filter(log => 
        (filter.severity === 'All' || log.severity === filter.severity) &&
        (filter.user === '' || log.user.toLowerCase().includes(filter.user.toLowerCase()))
    );

    const columns = getColumns(handleViewDetails);

    // --- RENDER COMPONENT ---
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header and Actions */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'headerSlide 0.8s ease-out forwards',
                    '@keyframes headerSlide': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="error.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <GavelIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    System Audit & Compliance Logs
                </Typography>
                <Button 
                    variant="contained" 
                    color="error" 
                    startIcon={<SecurityIcon />} 
                    onClick={() => console.log("Export Full Logs")}
                    sx={{ px: 3, py: 1, fontWeight: 600 }}
                >
                    Export Full Log File
                </Button>
            </Box>

            {/* Filtering Row */}
            <Paper 
                elevation={4} 
                sx={{ 
                    p: 2, 
                    mb: 4, 
                    borderRadius: 2, 
                    display: 'flex', 
                    gap: 3, 
                    alignItems: 'center',
                    // Animation: Slide-in from top
                    opacity: 0,
                    transform: 'translateY(20px)',
                    animation: 'filterSlideIn 1s ease-out 0.2s forwards',
                    '@keyframes filterSlideIn': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <FilterIcon color="action" />
                <Typography variant="body1" fontWeight={600} color="text.secondary">Filters:</Typography>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select
                        name="severity"
                        value={filter.severity}
                        label="Severity"
                        onChange={handleFilterChange}
                    >
                        <MenuItem value="All">All Severities</MenuItem>
                        <MenuItem value="Critical">Critical</MenuItem>
                        <MenuItem value="Warning">Warning</MenuItem>
                        <MenuItem value="Info">Info</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="User/Service Filter"
                    name="user"
                    value={filter.user}
                    onChange={handleFilterChange}
                    InputProps={{ startAdornment: <UserIcon color="action" sx={{ mr: 1 }} /> }}
                />
                
                <Tooltip title="View critical actions only">
                    <Button variant="outlined" color="error" size="small" sx={{ ml: 'auto' }}>
                        View Security Events
                    </Button>
                </Tooltip>
            </Paper>

            {/* Main Content: Logs DataGrid */}
            <Paper 
                elevation={10} 
                sx={{ 
                    height: { xs: 500, md: 700 }, // Responsive height
                    width: '100%', 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    p: { xs: 1, md: 3 }, // Responsive padding
                    // Animation: Slide-up effect
                    transform: 'translateY(20px)',
                    opacity: 0,
                    animation: 'slideUp 1s ease-out 0.4s forwards',
                    '@keyframes slideUp': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Box sx={{ height: '100%', width: '100%' }}>
                    <DataGrid
                        rows={filteredLogs}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                            sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
                        }}
                        disableRowSelectionOnClick
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: theme.palette.error.light,
                                color: theme.palette.text.primary,
                                fontWeight: 800,
                            },
                            '& .MuiDataGrid-row': {
                                transition: 'background-color 0.2s',
                                '&:hover': {
                                    backgroundColor: theme.palette.grey[100],
                                }
                            }
                        }}
                    />
                </Box>
            </Paper>

            {/* Log Details Dialog */}
            {selectedLog && (
                <LogDetailDialog 
                    open={openDialog} 
                    handleClose={() => setOpenDialog(false)} 
                    log={selectedLog} 
                    theme={theme}
                />
            )}
        </Container>
    );
};

export default AuditLogs;